import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Filter, FilterFrame, FilterSelection } from '../Filters.style';
import { FilterOption, FilterResultCounts, RangeResultsCounts } from '../../../types';
import { ExposedFiltersTypes, FilterTypes } from '../../../constants/initialFilters';
import FilterExposedHeader from '../FilterExposedHeader';
import { InteriorListWrapper, OptionWrapper, Image, Label } from './InteriorSelector.style';
import useAnimations from '../../../hooks/Animations/useAnimations';
import { AvailableFiltersCount } from '../../../types/oneGraphTypes';

interface InteriorSelectorTypes {
  filterType: string;
  interiorColorOptions: AvailableFiltersCount[];
  confirmedFilters: FilterTypes;
  openedFiltersList: ExposedFiltersTypes;
  handleAddFilter: (filterType: string, value: string) => void;
  handleExposeFilter: (arg: string) => void;
}

const InteriorSelector: React.FC<InteriorSelectorTypes> = ({
  filterType,
  interiorColorOptions,
  confirmedFilters,
  handleAddFilter,
  handleExposeFilter,
  openedFiltersList,
}) => {
  const { t } = useTranslation();

  const selectedInteriorColors = confirmedFilters.interiorColor;

  const handleAddInteriorFilter = (newInteriorColor: string) => {
    handleAddFilter('interiorColor', newInteriorColor);
  };

  const showFilter = openedFiltersList[filterType];

  const { dropdownAnimation, filtersAccordianAnimation } = useAnimations(showFilter);

  return (
    <Filter>
      <FilterExposedHeader
        filterType={filterType}
        selectedFilterLabels={selectedInteriorColors}
        handleExposeFilter={handleExposeFilter}
        openedFiltersList={openedFiltersList}
      />
      {dropdownAnimation(
        (props, item) =>
          item && (
            <FilterSelection style={props} data-testid="interior-filter-group">
              <FilterFrame style={filtersAccordianAnimation}>
                <InteriorListWrapper>
                  {interiorColorOptions.map((filterOption) => {
                    const { value, image } = filterOption;
                    const label = t(`vlp.filters.filterOptions.${filterType}.${value}`);
                    const isSelected = selectedInteriorColors.includes(value);
                    return (
                      <OptionWrapper
                        key={value}
                        type="button"
                        onClick={() => handleAddInteriorFilter(value)}
                        isSelected={isSelected}
                        data-testid={`interior-select-${value}`}
                      >
                        <Image
                          src={image as string}
                          aria-label={`${value} interior color`}
                          alt={`${value} interior color`}
                        />
                        <Label>{label}</Label>
                      </OptionWrapper>
                    );
                  })}
                </InteriorListWrapper>
              </FilterFrame>
            </FilterSelection>
          ),
      )}
    </Filter>
  );
};

export default InteriorSelector;
